/**
 * Legal Documents Page
 * Displays Terms and Conditions and Privacy Policy
 * This page is public and accessible without authentication
 */

import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import Card from '../components/Card'
import Button from '../components/Button'
import { FileText, Shield, CheckCircle2, Download, ArrowLeft } from 'lucide-react'
import { getTermsAndConditions, getPrivacyPolicy } from '../services/legalDocuments'
import { supabase } from '../services/supabase'

const LegalDocuments = () => {
  const { documentType } = useParams()
  const [document, setDocument] = useState(null)
  const [loading, setLoading] = useState(true)
  const [accepted, setAccepted] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [organizationId, setOrganizationId] = useState(null)

  useEffect(() => {
    // Check if user is authenticated and get organization ID (optional - for acceptance functionality)
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      setIsAuthenticated(!!session?.user)
      
      // If authenticated, try to get organization ID from user's organization memberships
      if (session?.user) {
        try {
          const { data: memberships, error } = await supabase
            .from('organization_members')
            .select('organization_id')
            .eq('user_id', session.user.id)
            .limit(1)
            .maybeSingle()
          
          if (!error && memberships) {
            setOrganizationId(memberships.organization_id)
          }
        } catch (error) {
          // User might not be in any organization, or table might not be accessible
          // This is fine - the page should still work without organization ID
          console.debug('Could not fetch organization ID:', error)
        }
      }
    }
    
    checkAuth()
    loadDocument()
  }, [documentType])

  const loadDocument = async () => {
    setLoading(true)
    try {
      let result
      if (documentType === 'terms' || documentType === 'terms-and-conditions') {
        result = await getTermsAndConditions()
      } else if (documentType === 'privacy' || documentType === 'privacy-policy') {
        result = await getPrivacyPolicy()
      }

      if (result?.success) {
        setDocument(result.data)
      }
    } catch (error) {
      console.error('Error loading document:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleAccept = async () => {
    // In a real implementation, this would record acceptance
    setAccepted(true)
    // Toast notification will be handled by the TermsAcceptanceModal component
  }

  const formatInlineMarkdown = (text) => {
    if (!text) return ''
    
    // Handle bold **text**
    const parts = text.split(/(\*\*[^*]+\*\*)/g)
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i} className="font-semibold text-gray-900 dark:text-white">{part.substring(2, part.length - 2)}</strong>
      }
      return <span key={i}>{part}</span>
    })
  }

  const formatMarkdownContent = (content) => {
    if (!content) return ''
    
    // Convert markdown to formatted JSX
    const lines = content.split('\n')
    const elements = []
    let currentList = []
    let listKey = 0
    
    lines.forEach((line, index) => {
      const trimmed = line.trim()
      
      // Headers
      if (trimmed.startsWith('# ')) {
        if (currentList.length > 0) {
          elements.push(<ul key={`list-${listKey++}`} className="list-disc list-inside mb-4 space-y-1 ml-4">{currentList}</ul>)
          currentList = []
        }
        elements.push(<h1 key={index} className="text-3xl font-bold mt-8 mb-4 text-gray-900 dark:text-white">{trimmed.substring(2)}</h1>)
        return
      }
      if (trimmed.startsWith('## ')) {
        if (currentList.length > 0) {
          elements.push(<ul key={`list-${listKey++}`} className="list-disc list-inside mb-4 space-y-1 ml-4">{currentList}</ul>)
          currentList = []
        }
        elements.push(<h2 key={index} className="text-2xl font-bold mt-6 mb-3 text-gray-900 dark:text-white">{trimmed.substring(3)}</h2>)
        return
      }
      if (trimmed.startsWith('### ')) {
        if (currentList.length > 0) {
          elements.push(<ul key={`list-${listKey++}`} className="list-disc list-inside mb-4 space-y-1 ml-4">{currentList}</ul>)
          currentList = []
        }
        elements.push(<h3 key={index} className="text-xl font-semibold mt-5 mb-2 text-gray-900 dark:text-white">{trimmed.substring(4)}</h3>)
        return
      }
      if (trimmed.startsWith('#### ')) {
        if (currentList.length > 0) {
          elements.push(<ul key={`list-${listKey++}`} className="list-disc list-inside mb-4 space-y-1 ml-4">{currentList}</ul>)
          currentList = []
        }
        elements.push(<h4 key={index} className="text-lg font-semibold mt-4 mb-2 text-gray-900 dark:text-white">{trimmed.substring(5)}</h4>)
        return
      }
      
      // Horizontal rule
      if (trimmed === '---') {
        if (currentList.length > 0) {
          elements.push(<ul key={`list-${listKey++}`} className="list-disc list-inside mb-4 space-y-1 ml-4">{currentList}</ul>)
          currentList = []
        }
        elements.push(<hr key={index} className="my-6 border-gray-300 dark:border-gray-700" />)
        return
      }
      
      // Bullet points
      if (trimmed.startsWith('- ')) {
        const text = trimmed.substring(2)
        currentList.push(<li key={`item-${currentList.length}`} className="mb-1">{formatInlineMarkdown(text)}</li>)
        return
      }
      
      // Empty lines - close list if open
      if (trimmed === '') {
        if (currentList.length > 0) {
          elements.push(<ul key={`list-${listKey++}`} className="list-disc list-inside mb-4 space-y-1 ml-4">{currentList}</ul>)
          currentList = []
        }
        elements.push(<br key={index} />)
        return
      }
      
      // Regular paragraphs
      if (currentList.length > 0) {
        elements.push(<ul key={`list-${listKey++}`} className="list-disc list-inside mb-4 space-y-1 ml-4">{currentList}</ul>)
        currentList = []
      }
      elements.push(<p key={index} className="mb-3 text-gray-700 dark:text-gray-300">{formatInlineMarkdown(line)}</p>)
    })
    
    // Close any remaining list
    if (currentList.length > 0) {
      elements.push(<ul key={`list-${listKey++}`} className="list-disc list-inside mb-4 space-y-1 ml-4">{currentList}</ul>)
    }
    
    return elements
  }

  const handleDownload = () => {
    // Create a downloadable version
    const content = document?.content || ''
    const blob = new Blob([content], { type: 'text/markdown' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${documentType === 'terms' ? 'Terms_and_Conditions' : 'Privacy_Policy'}.md`
    link.click()
    URL.revokeObjectURL(url)
  }

  if (loading) {
    return (
      <div className="section-container">
        <div className="text-center py-12">
          <p className="text-gray-600">Loading document...</p>
        </div>
      </div>
    )
  }

  const isTerms = documentType === 'terms' || documentType === 'terms-and-conditions'
  const title = isTerms ? 'Terms and Conditions' : 'Privacy Policy'
  const IconComponent = isTerms ? FileText : Shield

  return (
    <div className="section-container max-w-4xl mx-auto">
      {/* Back to login button for unauthenticated users */}
      {!isAuthenticated && (
        <div className="mb-6">
          <Link
            to="/login"
            className="inline-flex items-center text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
          >
            <ArrowLeft size={18} className="mr-2" />
            Back to Login
          </Link>
        </div>
      )}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center">
          <IconComponent className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold">{title}</h1>
          <p className="text-gray-600">
            {isTerms
              ? 'Terms and Conditions of Service'
              : 'Privacy Policy and Data Protection'}
          </p>
        </div>
      </div>

      <Card className="p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-sm text-gray-600">Last Updated</p>
            <p className="font-semibold">
              {document?.lastUpdated
                ? new Date(document.lastUpdated).toLocaleDateString('en-ZA', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
                : 'N/A'}
            </p>
          </div>
          <Button variant="outline" onClick={handleDownload}>
            <Download className="w-4 h-4 mr-2" />
            Download
          </Button>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-yellow-800">
            <strong>Important:</strong> This document is governed by the laws of the Republic of
            South Africa. By using our Service, you agree to be bound by these terms. Please read
            carefully.
          </p>
        </div>

        <div className="prose max-w-none prose-headings:font-bold prose-headings:text-gray-900 dark:prose-headings:text-gray-100 prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-strong:text-gray-900 dark:prose-strong:text-gray-100 prose-ul:text-gray-700 dark:prose-ul:text-gray-300">
          {document?.content ? (
            <div className="whitespace-pre-wrap text-sm leading-relaxed">
              {formatMarkdownContent(document.content)}
            </div>
          ) : isTerms ? (
            <TermsContent />
          ) : (
            <PrivacyContent />
          )}
        </div>

        {!accepted && organizationId && (
          <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-blue-900">Acceptance Required</p>
                <p className="text-sm text-blue-700">
                  Please read and accept the {title.toLowerCase()} to continue using the Service.
                </p>
              </div>
              <Button onClick={handleAccept} className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                I Accept
              </Button>
            </div>
          </div>
        )}

        {accepted && (
          <div className="mt-8 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            <p className="text-green-800 font-semibold">
              You have accepted the {title.toLowerCase()}.
            </p>
          </div>
        )}
      </Card>

      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Contact Information</h2>
        <div className="space-y-2 text-sm">
          <p>
            <strong>Company:</strong> CwickCraft Designs PTY (LTD)
          </p>
          <p>
            <strong>Email:</strong> <a href="mailto:legal@cwickcraft.com" className="text-primary-600 hover:underline">legal@cwickcraft.com</a>
          </p>
          <p>
            <strong>Address:</strong> Midrand, South Africa
          </p>
          <p>
            <strong>Phone (WhatsApp):</strong> <a href="https://wa.me/27746502694" className="text-primary-600 hover:underline">0746502694</a>
          </p>
        </div>
      </Card>
    </div>
  )
}

// Terms and Conditions Content Component
const TermsContent = () => {
  return (
    <div className="space-y-6 text-sm">
      <section>
        <h2 className="text-2xl font-bold mb-4">1. ACCEPTANCE OF TERMS</h2>
        <p>
          By accessing, using, or subscribing to the NaturAssist platform ("the
          Service", "the Platform", "we", "us", "our") operated by CwickCraft Designs PTY (LTD) ("the Company"),
          you ("the User", "Subscriber", "you", "your") agree to be bound by these Terms and
          Conditions ("Terms"). If you do not agree to these Terms, you must not use the Service.
        </p>
        <p className="mt-2">
          These Terms constitute a legally binding agreement between you and the Company, governed
          by the laws of the Republic of South Africa.
        </p>
      </section>

      <section>
        <h2 className="text-2xl font-bold mb-4">2. SUBSCRIPTION AND PAYMENT</h2>
        <p>
          The Service is offered on a subscription basis with various tiers. Subscription fees are
          charged monthly or annually as selected by the Subscriber. All fees are payable in
          advance and are non-refundable except as required by South African law.
        </p>
      </section>

      <section>
        <h2 className="text-2xl font-bold mb-4">3. LIMITATION OF LIABILITY</h2>
        <p className="font-semibold text-red-600">
          TO THE MAXIMUM EXTENT PERMITTED BY SOUTH AFRICAN LAW:
        </p>
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li>
            THE COMPANY'S TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT PAID BY THE SUBSCRIBER IN THE
            TWELVE (12) MONTHS PRECEDING THE CLAIM.
          </li>
          <li>
            THE COMPANY SHALL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR
            PUNITIVE DAMAGES.
          </li>
          <li>
            THE COMPANY SHALL NOT BE LIABLE FOR DAMAGES ARISING FROM CLINICAL DECISIONS MADE USING
            THE SERVICE.
          </li>
        </ul>
      </section>

      <section>
        <h2 className="text-2xl font-bold mb-4">4. INDEMNIFICATION</h2>
        <p className="font-semibold">
          SUBSCRIBERS AGREE TO INDEMNIFY, DEFEND, AND HOLD HARMLESS THE COMPANY FROM AND AGAINST
          ALL CLAIMS, DAMAGES, LOSSES, LIABILITIES, COSTS, AND EXPENSES ARISING FROM:
        </p>
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li>SUBSCRIBER'S USE OR MISUSE OF THE SERVICE.</li>
          <li>CLINICAL DECISIONS OR ACTIONS TAKEN BY SUBSCRIBERS.</li>
          <li>DATA ENTERED BY SUBSCRIBERS OR THEIR USERS.</li>
          <li>ANY CLAIMS BY SUBSCRIBER'S CLIENTS OR THIRD PARTIES.</li>
        </ul>
      </section>

      <section>
        <h2 className="text-2xl font-bold mb-4">5. MEDICAL DISCLAIMER</h2>
        <p className="font-semibold text-red-600">
          THE SERVICE IS A DATA MANAGEMENT TOOL AND DOES NOT PROVIDE MEDICAL ADVICE, DIAGNOSIS, OR
          TREATMENT.
        </p>
        <p className="mt-2">
          SUBSCRIBERS ARE SOLELY RESPONSIBLE FOR ALL CLINICAL DECISIONS AND MEDICAL JUDGMENTS. THE
          COMPANY SHALL NOT BE LIABLE FOR ANY ADVERSE OUTCOMES RESULTING FROM CLINICAL DECISIONS
          MADE USING THE SERVICE.
        </p>
      </section>

      <div className="mt-8 p-4 bg-gray-100 rounded">
        <p className="text-xs text-gray-600">
          <strong>Note:</strong> This is a summary. The full Terms and Conditions document contains
          all provisions. Please refer to the complete document for full details.
        </p>
      </div>
    </div>
  )
}

// Privacy Policy Content Component
const PrivacyContent = () => {
  return (
    <div className="space-y-6 text-sm">
      <section>
        <h2 className="text-2xl font-bold mb-4">1. INTRODUCTION</h2>
        <p>
          This Privacy Policy explains how we collect, use, disclose, and protect personal
          information in compliance with the Protection of Personal Information Act (POPIA) of
          South Africa and other applicable data protection laws.
        </p>
      </section>

      <section>
        <h2 className="text-2xl font-bold mb-4">2. INFORMATION WE COLLECT</h2>
        <p>We collect:</p>
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li>Account and billing information.</li>
          <li>Client data entered by Subscribers (health information, medical records).</li>
          <li>Usage data and device information.</li>
          <li>Information from third-party integrations.</li>
        </ul>
      </section>

      <section>
        <h2 className="text-2xl font-bold mb-4">3. DATA SUBJECT RIGHTS (POPIA)</h2>
        <p>Under POPIA, you have the right to:</p>
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li>Access your personal information.</li>
          <li>Request correction of inaccurate information.</li>
          <li>Request deletion of personal information.</li>
          <li>Object to processing of personal information.</li>
          <li>Lodge a complaint with the Information Regulator.</li>
        </ul>
      </section>

      <section>
        <h2 className="text-2xl font-bold mb-4">4. DATA SECURITY</h2>
        <p>
          We implement reasonable security measures to protect personal information. However, no
          method of transmission or storage is 100% secure. We cannot guarantee absolute security
          and are not liable for unauthorized access except where caused by gross negligence.
        </p>
      </section>

      <div className="mt-8 p-4 bg-gray-100 rounded">
        <p className="text-xs text-gray-600">
          <strong>Note:</strong> This is a summary. The full Privacy Policy document contains all
          provisions. Please refer to the complete document for full details.
        </p>
      </div>
    </div>
  )
}

export default LegalDocuments

